// @ts-strict-ignore
import {Component, computed, inject, Input, OnInit, signal} from "@angular/core";
import {Translations} from "../../../../../sim.translations.barrel";
import {AssessmentDataService} from "../services/assessment-data.service";
import {AssessmentContextService, AssessmentItemFormState} from "../services/assessment-context.service";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {TranslateService} from "@ngx-translate/core";
import {toSignal} from "@angular/core/rxjs-interop";
import {map, startWith} from "rxjs/operators";
import {AssessableUiService} from "../services/assessable-ui.service";
import {EChartsCoreOption} from "echarts/core";

function accumulatePercentages(accumulator: number, category: {percentage: number}) {
	return accumulator + category.percentage;
}

@UntilDestroy()
@Component({
	selector: "sim-competence-category-edit",
	templateUrl: "./competence-category-edit.component.html",
	styleUrl: "./competence-category-edit.component.scss",
	standalone: false,
})
export class CompetenceCategoryEditComponent implements OnInit {
	// Enums
	protected readonly T = Translations;

	// Injections
	private _dataService = inject(AssessmentDataService);
	private _contextService = inject(AssessmentContextService);
	private _translate = inject(TranslateService);
	protected uiService = inject(AssessableUiService);

	// Signals
	private readonly _competenceCategoryDistribution = signal<AssessmentItemFormState["competenceCategoryDistribution"]["value"]>([]);
	private readonly _totalPercentagesRaw = computed(() => this._competenceCategoryDistribution().reduce(accumulatePercentages, 0));
	public readonly competenceCategoryMap = toSignal(
		this._dataService.competenceCategories$.pipe(map((categories) => new Map(categories.map((category) => [category.id, category])))),
	);

	public readonly totalPercentages = computed(() => {
		const rawTotal = this._totalPercentagesRaw();

		if (10_000 - rawTotal <= 1 && 10_000 - rawTotal >= 0) {
			return 100;
		}

		return Number((rawTotal / 100).toFixed(2));
	});
	public readonly remainingPercentages = computed(() => {
		const rawRemaining = this.getRemainingPercentagesRaw();
		return Number((rawRemaining / 100).toFixed(2));
	});
	public readonly chartData = computed<EChartsCoreOption>(() => this._recalculateChartData());

	private getRemainingPercentagesRaw() {
		const rawTotal = this._totalPercentagesRaw();
		const rawRemaining = 10_000 - rawTotal;
		return rawRemaining <= 1 && rawRemaining >= 0 ? 0 : rawRemaining;
	}

	// Inputs
	@Input({required: true}) assessmentItemId: string;

	// Properties
	public categoryDistributionForm: AssessmentItemFormState["competenceCategoryDistribution"];
	public commonChartOptions: EChartsCoreOption = {
		title: {
			text: this._translate.instant(this.T.COMPETENCE_CATEGORY_PANEL_TITLE),
		},
		legend: {
			orient: "vertical",
			bottom: 0,
			left: 0,
			selectedMode: false,
		},
		series: [
			{
				type: "pie",
				radius: "90%",
				center: ["80%", "50%"],
				data: [],
				label: {
					show: false,
				},
			},
		],
	};

	public ngOnInit() {
		this.categoryDistributionForm = this._contextService.findItemControl(this.assessmentItemId).controls.competenceCategoryDistribution;

		this.categoryDistributionForm.valueChanges
			.pipe(startWith(this.categoryDistributionForm.value), untilDestroyed(this))
			.subscribe((value) => {
				this._competenceCategoryDistribution.set(value);
			});
	}

	public handleNumberInputValueChange($event: Event, i: number) {
		const input = $event.target as HTMLInputElement;
		const rawValue = Number(Number(input.value).toFixed(2));

		const value = Math.floor(rawValue * 100);
		this.categoryDistributionForm.controls.at(i).controls.percentage.setValue(value);
	}

	/*
	 * The percentage value is stored as a number between 0 and 10000 in the database.
	 * This function converts the value to a value between 0 and 100.
	 */
	public convertToPercentage(value: number) {
		return value / 100;
	}

	private _recalculateChartData(): EChartsCoreOption {
		const map = this.competenceCategoryMap();
		const percentageData = this._competenceCategoryDistribution().map((distributionEntry) => {
			const category = map.get(distributionEntry.competenceCategoryId);
			return {
				value: distributionEntry.percentage,
				name: category?.name ?? "N/A",
				itemStyle: {
					color: category?.color ?? "grey",
				},
			};
		});

		const rawRemaining = this.getRemainingPercentagesRaw();
		const remaining_name = `${this._translate.instant(this.T.COMPETENCE_CATEGORY_PANEL_REMAINING_PERCENTAGES)} ${this.remainingPercentages()}%`;

		const chartData = [...percentageData, {value: rawRemaining, name: remaining_name, itemStyle: {color: "#e57373"}}];
		const legendData = [...percentageData.map((entry) => entry.name), remaining_name];

		return {
			legend: {
				data: legendData,
			},
			series: [
				{
					data: chartData,
				},
			],
		};
	}
}
